"use client"

import { motion, useScroll, useTransform } from "framer-motion"
import { useRef } from "react"
import { Github, Mail } from "lucide-react"
import { TypingAnimation } from "./typing-animation"
import { MagneticButton } from "./magnetic-button"
import { ParticleField } from "./particle-field"

const roles = [
    "Hobby Developer",
    "Learning & Building",
    "Lofihaven.xyz Creator",
    "Code Enthusiast"
]

const socialLinks = [
    { icon: Github, href: "https://github.com/kevin-b-official", label: "GitHub" },
    { icon: Mail, href: "mailto:contact@horizov.site", label: "Email" },
]

export function UltraHero() {
    const containerRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end start"],
    })

    const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"])
    const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

    return (
        <section ref={containerRef} className="relative h-screen flex items-center justify-center overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 bg-background">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
                <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-primary/20 opacity-20 blur-[100px]" />
                <div className="absolute right-0 bottom-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-secondary/20 opacity-20 blur-[100px]" />
            </div>

            {/* Particle Field */}
            <ParticleField />

            <motion.div style={{ y, opacity }} className="relative z-10 text-center px-4">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="mb-8"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full border border-border bg-muted/10 text-sm font-medium backdrop-blur-sm">
                        🚀 Learning & Building
                    </span>
                </motion.div>

                <h1 className="text-6xl md:text-9xl font-bold tracking-tighter mb-4">
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-400 to-secondary animate-gradient">
                        Kevin B.
                    </span>
                </h1>

                {/* Typing Animation for Roles */}
                <div className="h-12 md:h-16 mb-8">
                    <TypingAnimation
                        words={roles}
                        className="text-xl md:text-3xl text-muted-foreground font-medium"
                        typingSpeed={80}
                        deletingSpeed={40}
                        pauseDuration={2500}
                    />
                </div>

                <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed">
                    Exploring the world of code, one project at a time.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
                    <MagneticButton strength={0.2}>
                        <a
                            href="#projects"
                            className="group relative px-8 py-4 bg-primary text-primary-foreground rounded-full font-medium overflow-hidden transition-transform hover:scale-105 active:scale-95 block"
                        >
                            <span className="relative z-10">View Work</span>
                            <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </a>
                    </MagneticButton>

                    <MagneticButton strength={0.2}>
                        <a
                            href="#contact"
                            className="px-8 py-4 rounded-full border border-border bg-muted/10 hover:bg-muted/20 transition-all hover:scale-105 active:scale-95 backdrop-blur-sm block"
                        >
                            Contact Me
                        </a>
                    </MagneticButton>
                </div>

                {/* Social Links */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                    className="flex gap-4 justify-center"
                >
                    {socialLinks.map((social, index) => (
                        <motion.div
                            key={social.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1 + index * 0.1 }}
                        >
                            <MagneticButton strength={0.4}>
                                <a
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-3 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 hover:bg-primary/10 transition-all block"
                                    aria-label={social.label}
                                >
                                    <social.icon className="w-5 h-5" />
                                </a>
                            </MagneticButton>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-32 left-1/2 -translate-x-1/2 text-muted-foreground"
            >
                <div className="w-6 h-10 rounded-full border-2 border-current flex justify-center p-1">
                    <div className="w-1 h-2 bg-current rounded-full" />
                </div>
            </motion.div>
        </section>
    )
}
